import { Context } from '@nuxt/types';
import { Config } from '../../../modules/channel/types/channel';
import rootConfig from '@root/config/root.config';
import type { WebBridge } from '@eg/delfi-app-bridge/dist/types/types';

function getApplication(ua: string) {
  let app = null;

  for (const application of rootConfig.applications) {
    if (ua.match(application.ua.pattern)) {
      app = application;
    }
  }

  return app;
}

async function getApplicationUsingBridge(bridge: WebBridge) {
  let app;
  let os = undefined;

  try {
    os = await bridge.request('device.os');
  } catch (e) {
    return undefined;
  }

  for (const application of rootConfig.applications) {
    if (application.os === os) {
      app = application;
    }
  }

  return app;
}

function compareSemVer(current: string, affected: string) {
  const currentParts = current.split('.').map((part) => parseInt(part, 10) || 0);
  const affectedParts = affected.split('.').map((part) => parseInt(part, 10) || 0);

  for (const index of [0, 1, 2] as const) {
    if (currentParts[index] > affectedParts[index]) {
      return 1;
    }

    if (currentParts[index] < affectedParts[index]) {
      return -1;
    }
  }

  return 0;
}

function isAffectedVersion(appVersion: string | null, affectedVersion: string) {
  if (!appVersion) {
    return false;
  }

  if (!affectedVersion.includes('.')) {
    return appVersion === affectedVersion;
  }

  return compareSemVer(appVersion, affectedVersion) >= 0;
}

async function initApplicationType(req: Context['req'], appBridge: WebBridge | null) {
  const ua: string | undefined | null = process.server ? req.headers['user-agent'] : navigator.userAgent;

  const rootApplication: Config['settings']['application'] = {
    type: null,
    appName: null,
    appVersion: null,
    isSearchBot: false,
    specialFeatures: {
      forceArticleRefetch: false,
      adFree: false,
      disableHeader: false,
      disableConsentModal: false,
      hideSignupAndOrders: {
        enabled: false,
        affectedApps: null,
      },
    },
  };

  if (!ua) {
    return rootApplication;
  }

  let application: any;

  // Resolve application using app bridge is available
  if (appBridge) {
    const [bridgeApplication, slug, version, isReviewMode] = await Promise.all([
      getApplicationUsingBridge(appBridge),
      appBridge.request('app.slug'),
      appBridge.request('app.version'),
      appBridge.request('app.isReviewMode'),
    ]);

    application = bridgeApplication;

    if (slug) {
      rootApplication.appName = slug;
    }

    if (version) {
      rootApplication.appVersion = version;
    }

    if (isReviewMode) {
      rootApplication.specialFeatures.hideSignupAndOrders.enabled = true;
    }
  }

  // If app bridge didn't resolve use user agent as fallback
  if (!application) {
    application = getApplication(ua);
    const appUARegexResult = application?.ua.pattern.exec(ua);
    rootApplication.appName = (application?.ua.channelCaptureGroupIndex && appUARegexResult?.[application.ua.channelCaptureGroupIndex]) || null;
    rootApplication.appVersion = (application?.ua.versionCaptureGroupIndex && appUARegexResult?.[application.ua.versionCaptureGroupIndex]) || null;
    rootApplication.specialFeatures = application?.specialFeatures || rootApplication.specialFeatures;

    const { hideSignupAndOrders } = rootApplication.specialFeatures;
    const { affectedApps } = hideSignupAndOrders;

    if (hideSignupAndOrders.enabled && affectedApps?.length) {
      let hideSignupAndOrdersResult = false;

      for (const affectedApp of affectedApps) {
        const isSameVersion = !!affectedApp.version && isAffectedVersion(rootApplication.appVersion, affectedApp.version);
        const isSameChannel = rootApplication.appName === affectedApp.channel;

        if (affectedApp.channel && affectedApp.version) {
          if (isSameVersion && isSameChannel) {
            hideSignupAndOrdersResult = true;
          }
        } else if (affectedApp.channel && isSameChannel) {
          hideSignupAndOrdersResult = true;
        } else if (affectedApp.version && isSameVersion) {
          hideSignupAndOrdersResult = true;
        }
      }

      rootApplication.specialFeatures.hideSignupAndOrders.enabled = hideSignupAndOrdersResult;
    }
  }

  rootApplication.isSearchBot = !!ua.toLowerCase().match(rootConfig.searchBotsRegex);
  rootApplication.type = application?.type || null;

  if (rootApplication.isSearchBot) {
    rootApplication.specialFeatures.disableConsentModal = true;
    rootApplication.specialFeatures.forceArticleRefetch = true;
  }

  return rootApplication;
}

export default initApplicationType;
